/**
 * Converts a GeoJSON boundary Feature (as served by the Mapi CDN, e.g.
 * https://mapi.zg-api.com/region/20330.geojson) into the encoded-polyline
 * MultiPolygon shape the HotPads boundary pipeline already consumes
 * (`BoundaryData.coordinates`: one encoded ring string per outer ring/hole).
 * Keeping the wire shape identical means the map draws CDN boundaries through
 * the exact same decode/render path as API boundaries.
 */

import { encodePolylineFromCoords } from '@/src/lib/utils/polylineUtils';

/** GeoJSON position — longitude first, latitude second. */
type GeoJsonPosition = [longitude: number, latitude: number];
type GeoJsonRing = Array<GeoJsonPosition>;
type GeoJsonPolygon = Array<GeoJsonRing>;

export type GeoJsonBoundaryFeature = {
  type?: string;
  geometry?: {
    type?: string;
    coordinates?: unknown;
  };
};

export type EncodedGeoJsonBoundary = {
  type: 'MultiPolygon';
  coordinates: Array<Array<string>>;
};

function toLatLng([longitude, latitude]: GeoJsonPosition): { lat: number; lng: number } {
  return { lat: latitude, lng: longitude };
}

/**
 * The feature's polygon rings as encoded polylines, or `null` when it carries
 * no polygon geometry. A `Polygon` normalizes to a single-polygon MultiPolygon.
 */
export function geoJsonFeatureToEncodedBoundary(
  feature: GeoJsonBoundaryFeature | null | undefined,
): EncodedGeoJsonBoundary | null {
  const geometry = feature?.geometry;
  if (!geometry?.coordinates) {
    return null;
  }
  const polygons =
    geometry.type === 'MultiPolygon'
      ? (geometry.coordinates as Array<GeoJsonPolygon>)
      : geometry.type === 'Polygon'
        ? [geometry.coordinates as GeoJsonPolygon]
        : null;
  if (!polygons?.length) {
    return null;
  }
  const coordinates = polygons.map((rings) => rings.map((ring) => encodePolylineFromCoords(ring.map(toLatLng))));
  return { type: 'MultiPolygon', coordinates };
}
